import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDocumentos, downloadDocumento } from '../redux/documentosSlice';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Accordion,
  Button,
  Alert,
  Modal,
  Spinner,
  ButtonGroup
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import '../styles/DocumentosPage.css';

// Configuración de PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentosPage = () => {
  const dispatch = useDispatch();
  const { documentos, loading: loadingRedux, error } = useSelector((state) => state.documentos);
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [pdfKey, setPdfKey] = useState(0);
  const [loadingView, setLoadingView] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState(600);
  const [documentType, setDocumentType] = useState('pdf');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Funciones de zoom
  const zoomIn = () => {
    const newScale = scale + 0.25;
    setScale(newScale);
    setPageWidth(600 * newScale);
  };

  const zoomOut = () => {
    if (scale > 0.5) {
      const newScale = scale - 0.25;
      setScale(newScale);
      setPageWidth(600 * newScale);
    }
  };

  const resetZoom = () => {
    setScale(1.0);
    setPageWidth(600);
  };

  useEffect(() => {
    dispatch(fetchDocumentos());
  }, [dispatch]);

  useEffect(() => {
    if (error) setShowError(true);
  }, [error]);

  const getDocumentType = (url) => {
    if (!url) return 'pdf';
    const extension = url.split('.').pop().toLowerCase();
    if (['docx', 'doc', 'docm', 'dotx', 'dot', 'odt', 'ocx'].includes(extension)) return 'word';
    if (['xlsx', 'xls', 'xlsm', 'xlsb', 'xltx', 'xlt', 'xlam', 'csv', 'lsx'].includes(extension)) return 'excel';
    if (['pptx', 'ppt', 'pptm', 'potx', 'ppsx'].includes(extension)) return 'powerpoint';
    if (['pdf'].includes(extension)) return 'pdf';
    return 'other';
  };

  const getDocumentTypeName = (type) => {
    const types = {
      'word': 'Word',
      'excel': 'Excel',
      'powerpoint': 'PowerPoint',
      'pdf': 'PDF',
      'other': 'Archivo'
    };
    return types[type] || type.toUpperCase();
  };

  const getDocumentIcon = (type) => {
    const icons = {
      'word': 'bi-file-word-fill text-primary',
      'excel': 'bi-file-excel-fill text-success',
      'powerpoint': 'bi-file-ppt-fill text-danger',
      'pdf': 'bi-file-pdf-fill text-danger',
      'other': 'bi-file-earmark-fill text-secondary'
    };
    return icons[type] || 'bi-file-earmark-fill text-secondary';
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoadError(null);
  };

  const handleRetry = () => {
    setShowError(false);
    dispatch(fetchDocumentos());
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentDocument(null);
    setPdfLoadError(null);
    setPdfKey(prevKey => prevKey + 1);
  };

  const handleDownload = async (id) => {
    try {
      setLoadingDownload(true);
      const result = await dispatch(downloadDocumento(id));
      if (downloadDocumento.fulfilled.match(result)) {
        await Swal.fire({
          title: '¡Descarga exitosa!',
          text: `El documento ${result.payload.fileName} se ha descargado correctamente`,
          icon: 'success',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.message || 'No se pudo descargar el documento',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setLoadingDownload(false);
    }
  };

  const handleView = async (documento) => {
    try {
      setLoadingView(true);
      const type = getDocumentType(documento.url);
      setDocumentType(type);
      
      if (type === 'pdf') {
        setCurrentDocument({
          ...documento,
          type: 'pdf',
          fileUrl: {
            url: `https://api.sinterec.cl/api/documentos/ver-pdf/${documento.id}`,
            httpHeaders: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        });
      } else {
        const fileName = documento.url.split('/').pop();
        setCurrentDocument({
          ...documento,
          type: type,
          fileUrl: `https://api.sinterec.cl/api/documentos/office-proxy?url=/public/pdf/${encodeURIComponent(fileName)}`
        });
      }
      
      setShowModal(true);
    } catch (error) {
      console.error('Error al visualizar:', error);
      setPdfLoadError('No se pudo cargar el documento');
    } finally {
      setLoadingView(false);
    }
  };

  const renderOfficeViewer = () => {
    if (!currentDocument?.fileUrl) return null;
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentDocument.fileUrl)}`;
    return (
      <div style={{ height: '100%', width: '100%' }}>
        <iframe
          title={`${documentType} Viewer`}
          src={officeViewerUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          style={{ border: 'none', minHeight: '500px' }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          allowFullScreen
        />
        <div className="text-muted small mt-2">
          Si el documento no se carga, intenta <Button variant="link" size="sm" onClick={() => window.location.reload()}>recargar</Button>.
        </div>
      </div>
    );
  };

  const renderDocumentViewer = () => {
    if (!currentDocument) {
      return (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <p>Cargando documento...</p>
        </div>
      );
    }
  
    if (pdfLoadError) {
      return (
        <Alert variant="danger" className="text-center">
          {pdfLoadError}
          <Button
            variant="link"
            onClick={() => handleView(currentDocument)}
            className="mt-2"
            disabled={loadingView}
          >
            {loadingView ? <Spinner animation="border" size="sm" /> : 'Reintentar'}
          </Button>
        </Alert>
      );
    }
  
    switch (documentType) {
      case 'pdf':
        return (
          <Document
            key={pdfKey}
            file={currentDocument.fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error detallado:", error);
              setPdfLoadError('Error al cargar el PDF. Intente descargarlo.');
            }}
            loading={
              <div className="text-center my-4">
                <Spinner animation="border" />
                <p>Cargando documento...</p>
              </div>
            }
          >
            {Array.from({ length: numPages || 0 }, (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={pageWidth}
                loading={
                  <div className="text-center my-2">
                    <Spinner animation="border" size="sm" />
                    <p>Cargando página {index + 1}...</p>
                  </div>
                }
              />
            ))}
          </Document>
        );
      case 'word':
      case 'excel':
      case 'powerpoint':
        return renderOfficeViewer();
      default:
        return (
          <Alert variant="warning">
            Formato de documento no soportado para visualización.
            <div className="mt-2">
              <Button
                variant="primary"
                onClick={() => handleDownload(currentDocument.id)}
              >
                Descargar Documento
              </Button>
            </div>
          </Alert>
        );
    }
  };

  return (
    <div className="documentos-container">
      {/* Header con logo y título */}
      <div className="documentos-header">
        <img 
          src="https://api.sinterec.cl/public/images/SINTEREC_logo.jpg" 
          alt="Logo SINTEREC" 
          className="logo-sinterec"
        />
        <h1 className="documentos-title">Documentos</h1>
      </div>

      {isMobile && (
        <Alert variant="warning" className="mobile-warning">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Para una mejor experiencia al visualizar documentos, se recomienda utilizar un dispositivo de escritorio.
          Algunos documentos podrían no mostrarse correctamente en dispositivos móviles.
        </Alert>
      )}

      {showError && (
        <Alert variant="danger" dismissible onClose={() => setShowError(false)}>
          <strong>Error:</strong> {error.message || 'Error al cargar documentos'}
          <div className="mt-2">
            <Button variant="secondary" onClick={handleRetry}>
              Intentar nuevamente
            </Button>
          </div>
        </Alert>
      )}

      {loadingRedux && !documentos.length && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      )}

      {!loadingRedux && documentos.length === 0 && !error && (
        <Alert variant="info">No hay documentos disponibles</Alert>
      )}

      <Accordion className="documentos-accordion">
        {documentos.map((documento, index) => (
          <Accordion.Item eventKey={index.toString()} key={documento.id} className="documento-item">
            <Accordion.Header className="documento-header">
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center">
                  <i className={`bi ${getDocumentIcon(getDocumentType(documento.url))} documento-icon me-3`}></i>
                  <span className="documento-title">{documento.titulo || 'Documento sin título'}</span>
                </div>
                <div className="text-muted documento-date">
                  {new Date(documento.fecha_subida).toLocaleDateString()} - {getDocumentTypeName(getDocumentType(documento.url))}
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body className="documento-body">
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  onClick={() => handleView(documento)}
                  disabled={loadingRedux || loadingView}
                  className="documento-btn"
                >
                  {loadingView && currentDocument?.id === documento.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <i className="bi bi-eye-fill me-1"></i> Ver
                    </>
                  )}
                </Button>

                <Button
                  variant="outline-success"
                  onClick={() => handleDownload(documento.id)}
                  disabled={loadingRedux || loadingDownload}
                  className="documento-btn"
                >
                  {loadingDownload && currentDocument?.id === documento.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <i className="bi bi-download me-1"></i> Descargar
                    </>
                  )}
                </Button>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered fullscreen="lg-down">
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            {currentDocument?.titulo || 'Documento'}
            {documentType === 'pdf' && (
              <div className="float-end">
                <ButtonGroup size="sm" className="ms-2">
                  <Button variant="outline-secondary" onClick={zoomOut} disabled={scale <= 0.5}>
                    <i className="bi bi-zoom-out"></i>
                  </Button>
                  <Button variant="outline-secondary" onClick={resetZoom}>
                    {Math.round(scale * 100)}%
                  </Button>
                  <Button variant="outline-secondary" onClick={zoomIn}>
                    <i className="bi bi-zoom-in"></i>
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh', overflow: 'auto' }}>
          {renderDocumentViewer()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => currentDocument && handleDownload(currentDocument.id)}
            disabled={loadingDownload}
          >
            {loadingDownload ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Descargando...
              </>
            ) : (
              'Descargar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentosPage;